// Top-level router. Public routes (Home, Designer, DTF) allow guest access;
// Admin is auth-gated and nested under <AdminShell /> so every admin page
// shares the sidebar + chrome. Customer login is enforced inside the checkout
// step of each builder, not at the page boundary — so designs are quick to start.
import { Routes, Route, Navigate } from 'react-router-dom'
import Home from './pages/Home'
import Designer from './pages/Designer'
import DTF from './pages/DTF'
import Login from './pages/Login'
import OrderConfirm from './pages/OrderConfirm'
import AdminShell from './components/AdminShell'
import AdminOverview from './pages/admin/Overview'
import AdminOrders from './pages/admin/Orders'
import ProcessingDashboard from './pages/admin/ProcessingDashboard'
import AdminOrderDetail from './pages/admin/OrderDetail'
import AdminCustomers from './pages/admin/Customers'
import CustomerDetail from './pages/admin/CustomerDetail'
import ArtworkApproval from './pages/admin/ArtworkApproval'
import ProductionQueue from './pages/admin/Production'
import Shipping from './pages/admin/Shipping'
import AbandonedCarts from './pages/admin/AbandonedCarts'
import Payments from './pages/admin/Payments'
import Inventory from './pages/admin/Inventory'
import Quotes from './pages/admin/Quotes'
import PurchaseOrders from './pages/admin/PurchaseOrders'
import Statements from './pages/admin/Statements'
import Companies from './pages/admin/Companies'
import Users from './pages/admin/Users'
import SettingsPrintsPage from './pages/admin/SettingsPrintsPage'
import SettingsWebPage from './pages/admin/SettingsWebPage'
import CreateMockup from './pages/admin/CreateMockup'
import GarmentResizer from './pages/admin/GarmentResizer'
import Garments from './pages/admin/Garments'
import GarmentDetail from './pages/admin/GarmentDetail'
import StudioMedia from './pages/admin/StudioMedia'
import AiArtwork from './pages/admin/AiArtwork'
import SettingsStripe from './pages/admin/SettingsStripe'
import SettingsHowTo from './pages/admin/SettingsHowTo'
import Profile from './pages/Profile'
import AiLearning from './pages/admin/AiLearning'
import PlatformIdeas from './pages/admin/PlatformIdeas'
import IdeasWidget from './components/IdeasWidget'
import { useEffect, useState } from 'react'
import { useAuth } from '@workos-inc/authkit-react'

// Generic auth gate. Trusts the SERVER (the evrst_prints_session sealed
// cookie set by /auth/callback) as the source of truth — not AuthKit React's
// in-browser state, which can be out of sync if the user signed in via the
// /auth/login server flow rather than the SDK's hosted flow. Falls back to
// AuthKit React's user as a positive signal so we don't block users mid-
// SDK-flow. The `next` query param hints where to send the user after a
// successful sign-in (e.g. /login?next=/designer).
function AuthGate({ next, children }: { next: string; children: React.ReactNode }) {
  const { user: authkitUser, isLoading: authkitLoading } = useAuth()
  const [serverSignedIn, setServerSignedIn] = useState<boolean | null>(null)

  useEffect(() => {
    let mounted = true
    fetch('/api/me', { credentials: 'include' })
      .then((r) => r.json())
      .then((d) => { if (mounted) setServerSignedIn(!!d?.authenticated) })
      .catch(() => { if (mounted) setServerSignedIn(false) })
    return () => { mounted = false }
  }, [])

  if (authkitLoading || serverSignedIn === null) return <div className="p-10 text-ink-3 text-sm">Loading…</div>
  if (!authkitUser && !serverSignedIn) return <Navigate to={`/login?next=${encodeURIComponent(next)}`} replace />
  return <>{children}</>
}

// Admin-flavoured gate — pin a fixed /admin next URL so retried sign-ins
// always return to the admin shell.
function AdminGate({ children }: { children: React.ReactNode }) {
  return <AuthGate next="/admin">{children}</AuthGate>
}

export default function App() {
  return (
    <>
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/designer" element={<AuthGate next="/designer"><Designer /></AuthGate>} />
      <Route path="/dtf" element={<AuthGate next="/dtf"><DTF /></AuthGate>} />
      <Route path="/orders/:id" element={<OrderConfirm />} />
      <Route path="/profile" element={<Profile />} />
      <Route path="/login" element={<Login />} />

      {/* Admin — nested under the shell. Every leaf renders inside <Outlet /> in AdminShell. */}
      <Route path="/admin" element={<AdminGate><AdminShell /></AdminGate>}>
        <Route index element={<AdminOverview />} />

        {/* Processing */}
        <Route path="processing"      element={<ProcessingDashboard />} />
        <Route path="quotes"          element={<Quotes />} />
        <Route path="orders"          element={<AdminOrders />} />
        <Route path="orders/:id"      element={<AdminOrderDetail />} />
        <Route path="purchase-orders" element={<PurchaseOrders />} />
        <Route path="abandoned-carts" element={<AbandonedCarts />} />
        <Route path="payments"        element={<Payments />} />

        {/* Production */}
        <Route path="artwork-approval" element={<ArtworkApproval />} />
        <Route path="production"       element={<ProductionQueue />} />
        <Route path="shipping"         element={<Shipping />} />
        <Route path="inventory"        element={<Inventory />} />
        <Route path="create-mockup"    element={<CreateMockup />} />
        <Route path="garment-resizer"  element={<GarmentResizer />} />

        {/* Catalog */}
        <Route path="garments"      element={<Garments />} />
        <Route path="garments/:id"  element={<GarmentDetail />} />
        <Route path="studio-media"  element={<StudioMedia />} />
        <Route path="ai-artwork"    element={<AiArtwork />} />

        {/* Customers */}
        <Route path="customers"      element={<AdminCustomers />} />
        <Route path="customers/:id"  element={<CustomerDetail />} />
        <Route path="companies"  element={<Companies />} />
        <Route path="statements" element={<Statements />} />

        {/* Settings */}
        <Route path="settings/prints-page"    element={<SettingsPrintsPage />} />
        <Route path="settings/web-page"       element={<SettingsWebPage />} />
        <Route path="settings/users"          element={<Users />} />
        <Route path="settings/stripe"         element={<SettingsStripe />} />
        <Route path="settings/ai-learning"    element={<AiLearning />} />
        <Route path="settings/platform-ideas" element={<PlatformIdeas />} />
        <Route path="settings/how-to"         element={<SettingsHowTo />} />

        <Route path="*" element={<Navigate to="/admin" replace />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>

      {/* IDEAS — floating feedback widget, rendered across every route so
          public + signed-in users can submit. Hides itself when an admin
          turns it off in Settings → Platform Ideas. */}
      <IdeasWidget />
    </>
  )
}
